<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue'
import { dragscroll } from 'vue-dragscroll'
import type { ColorGroupSwatchesProps } from '.'
import {
  getProductSwatches,
  queryGroupProducts,
  updateProductCardSwatch,
  type ColorSwatch,
  type Product
} from './color-group-swatches.utils'

const { product, pdpNavigate, triggerRequired, wrapperType, allowSingleSwatch } = defineProps<{
  product: ColorGroupSwatchesProps['product']
  pdpNavigate: ColorGroupSwatchesProps['pdpNavigate']
  triggerRequired: ColorGroupSwatchesProps['triggerRequired']
  wrapperType: ColorGroupSwatchesProps['wrapperType']
  allowSingleSwatch: ColorGroupSwatchesProps['allowSingleSwatch']
}>()

const vDragScroll = dragscroll

const container = ref<HTMLElement | null>(null)
const isOpen = ref(false)

const groupProducts = ref<Product[]>([])
const swatches = computed(() => getProductSwatches(groupProducts.value))
const hasMultipleSwatches = computed(() => swatches.value.length > 1)

onBeforeMount(async () => {
  const groupTag = product.tags.find((tag) => tag.startsWith('group:'))?.trim()

  groupProducts.value = await queryGroupProducts(groupTag ?? '')
})

const toggleSwatches = () => {
  isOpen.value = !isOpen.value

  if (isOpen.value) {
    setTimeout(() => {
      const selectedEl = container.value ? (container.value.querySelector('[tabindex="0"]') as HTMLElement) : null

      selectedEl?.focus()
    }, 1000)
  }
}

const changeSwatch = (e: Event, swatch: ColorSwatch) => {
  if (pdpNavigate) return

  e.preventDefault()

  if (wrapperType === 'productCard') {
    const wrapperEl = container.value ? (container.value.closest('product-card') as HTMLElement) : null

    updateProductCardSwatch(swatch, wrapperEl)

    setTimeout(() => {
      const el = document.querySelector(`[handle="${swatch.productHandle}"`)

      if (el) {
        const linkEl = el?.querySelector<HTMLElement>('a')

        linkEl?.focus()
      }
    }, 1000)
  }
}
</script>

<template>
  <template v-if="allowSingleSwatch || hasMultipleSwatches">
    <button
      v-if="triggerRequired"
      class="color-group-swatches__trigger"
      :class="{ 'is-open': isOpen }"
      type="button"
      @click="toggleSwatches"
    >
      {{ swatches.length }} Colors
    </button>
    <div
      ref="container"
      v-drag-scroll
      class="color-group-swatches__list no-scrollbar"
      :class="{ 'trigger-required': triggerRequired, 'is-open': isOpen }"
    >
      <a
        v-for="swatch in swatches"
        :key="swatch.name"
        :aria-label="swatch.name"
        :href="swatch.productUrl"
        class="tw-block tw-aspect-square tw-w-9 tw-min-w-9 tw-select-none tw-border tw-border-solid"
        :class="swatch.productHandle === product.handle ? 'tw-border-[currentColor]' : 'tw-border-[rgba(0,0,0,0.1)]'"
        :style="{ background: swatch.background }"
        @click="changeSwatch($event, swatch)"
      ></a>
    </div>
  </template>
</template>
